import type { CatalogEntry } from '../types';

/**
 * Auth (feature `01`) error codes + catalog.
 *
 * Code shape is `FFAAEE` (zero-padded decimal): `FF=01` (auth), `AA` = the
 * endpoint within auth (`00` = feature-level / cross-endpoint), `EE` = the
 * specific condition. `AUTH_CODES` is the namespaced accessor referenced in
 * business code (`ErrorCode.Auth.VerifyCode.InvalidOrExpired`); `AUTH_CATALOG`
 * maps each code to its `{ httpStatus, message }` (the default user-facing
 * prose; dynamic prose is folded in per-throw via `messageOverride`).
 *
 * Endpoint → `AA` map:
 *   00 feature-level     01 requestMagicLink   02 verifyCode      03 verifyLinkToken
 *   04 completeProfile   06 oauthCallback      07 oauthOneTap
 *   08 socialLogin(link) — RETIRED 2026-09-09, connected-logins feature removed; do NOT reuse 08
 *   09 oauthExchange     10 refresh           11 switchWorkspace  12 acceptInvite
 *   13 acceptShareableLink  15 revokeSession   18 deleteAccount   19 loginWithPassword
 *   21 resetPassword     22 signupWithPassword  23 changeEmail (private support route)
 *   (forgotPassword throws nothing — see AUTH_CODES)
 */
export const AUTH_CODES = {
  /** Cross-endpoint, not specific to a single endpoint (AA = 00). */
  Session: {
    /** No org membership found while building an auth response. */
    NoOrgMembership: '010003',
    /** No workspace membership found while building an auth response. */
    NoWorkspaceMembership: '010004',
    /** Operation requires an active session but the token carries none. */
    SessionRequired: '010005',
    /** The JWT's sessionId points at a `UserSession` row that was revoked
     *  (Settings > Security "log out" / "revoke all") or no longer exists —
     *  checked on every request in `JwtStrategy.validate`. */
    Revoked: '010009',
    /**
     * Completed-profile, non-OWNER user with an org membership but zero live
     * workspace memberships — reached only by being removed from every
     * workspace. Distinct from `NoWorkspaceMembership`, reserved for genuinely corrupt accounts.
     */
    RemovedFromWorkspace: '010010',
  },
  /** Workspace-validity interceptor (feature-level, AA = 00). */
  WorkspaceContext: {
    DeletedNoAlternative: '010001',
  },
  /** Live workspace-membership guard (feature-level, AA = 00). */
  WorkspaceAccess: {
    /** Caller's WorkspaceMember row is gone (removed / workspace deleted). */
    NoLongerMember: '010002',
    /** Caller's workspace role is below the route's required level. */
    InsufficientRole: '010006',
  },
  /** Org-role guard (feature-level, AA = 00). */
  OrgAccess: {
    /** Caller's org role is below the route's required level. */
    InsufficientRole: '010007',
  },
  /** Cron-validation-token guard for internal endpoints (feature-level, AA = 00). */
  CronAccess: {
    /** Missing/invalid `x-cron-validation-token`, or the token is unconfigured. */
    InvalidToken: '010008',
  },
  /** Cloudflare Turnstile guard on the public password routes (feature-level, AA = 00). */
  Captcha: {
    /**
     * Missing token, token rejected by Cloudflare, or Cloudflare unreachable —
     * one code on purpose (the guard fails closed and never says which).
     */
    VerificationFailed: '010011',
  },
  RequestMagicLink: {
    RateLimited: '010101',
    /** Link resolves and the requesting email already belongs to a different org. */
    ShareableLinkAlreadyInDifferentOrg: '010102',
    /** Invite resolves and the requesting email already belongs to a different org. */
    InviteAlreadyInDifferentOrg: '010103',
  },
  VerifyCode: {
    InvalidOrExpired: '010201',
    AttemptsExceeded: '010202',
    RateLimited: '010203',
    /** Defense-in-depth re-check against the request→verify race. */
    ShareableLinkAlreadyInDifferentOrg: '010204',
    /** Same request→verify re-check, for an invite instead of a shareable link. */
    InviteAlreadyInDifferentOrg: '010205',
  },
  VerifyLinkToken: {
    InvalidOrExpired: '010301',
  },
  CompleteProfile: {
    InvalidPendingToken: '010401',
    InviteInvalid: '010402',
    TrialPlanNotSeeded: '010403',
  },
  /** OAuth callback (AA = 06) — browser-redirect callbacks had no prior code group. */
  OauthCallback: {
    ShareableLinkAlreadyInDifferentOrg: '010601',
    InviteAlreadyInDifferentOrg: '010602',
  },
  OauthExchange: {
    InvalidToken: '010901',
    UserNotFound: '010902',
  },
  /** Google One Tap credential verification (AA = 07). */
  OauthOneTap: {
    InvalidCredential: '010701',
    RateLimited: '010702',
  },
  Refresh: {
    /**
     * Every refresh failure collapses to this single code on purpose — the
     * client must not be able to distinguish *why* a refresh failed.
     */
    Invalid: '011001',
  },
  SwitchWorkspace: {
    NotFound: '011101',
    NotInOrg: '011102',
    NotMember: '011103',
  },
  AcceptInvite: {
    Invalid: '011201',
    AlreadyInDifferentOrg: '011202',
    UserNotFound: '011203',
    NotForYou: '011204',
  },
  AcceptShareableLink: {
    Invalid: '011301',
    AlreadyInDifferentOrg: '011302',
    UserNotFound: '011303',
  },
  RevokeSession: {
    CannotRevokeCurrent: '011501',
    NotFound: '011502',
  },
  DeleteAccount: {
    UserNotFound: '011801',
    LastOrgOwner: '011802',
  },
  /** Password login — step 1, exchanging email+password for an emailed OTP (AA = 19). */
  LoginWithPassword: {
    /**
     * Unknown email, no password set on the account, or a wrong password — all
     * three collapse to this one code on purpose. Distinguishing them would let
     * a caller enumerate which addresses have accounts and which of those carry
     * a migrated password.
     */
    InvalidCredentials: '011901',
    RateLimited: '011902',
  },
  /** "Forgot password" — step 2, consuming the link and setting the password (AA = 21). */
  ResetPassword: {
    InvalidOrExpired: '012101',
  },
  /** Email+password signup (AA = 22). */
  SignupWithPassword: {
    /**
     * The address is unusable for a password signup: it already has a verified
     * account, or it has one created by magic link / OAuth (which may never
     * gain a password — see `AuthMethod`). Both collapse to one deliberately
     * vague code so this route cannot be used to discover which addresses are
     * registered, or by which method.
     */
    InvalidDetails: '012201',
    RateLimited: '012202',
    /** OTP verification: wrong, expired, or already-consumed code. */
    InvalidOrExpiredCode: '012203',
    /**
     * The address's domain is on `BLOCKED_EMAIL_DOMAINS` (@trulyinbox/types).
     *
     * Distinct from `InvalidDetails` (012201), which is deliberately vague so
     * the route cannot be used to discover which addresses are registered. That
     * reasoning does not apply here: the blocklist is a static property of the
     * DOMAIN, identical for every visitor and revealing nothing about who has an
     * account — so being specific costs no privacy and saves the user from
     * retrying an address that can never work.
     */
    BlockedEmailDomain: '012204',
    /**
     * The address is a `+tag` alias (`jane+x@example.com`) on a domain that
     * doesn't allow them (only `saleshandy.com` does). Specific for the same
     * reason as `BlockedEmailDomain`: a static property of the address shape,
     * revealing nothing about who is registered.
     */
    PlusAliasNotAllowed: '012205',
  },
  /**
   * Support-only login-email change, `POST /private/users/change-email`
   * (PrivateApiTokenGuard, AA = 23). Specific codes on purpose: the caller is
   * support tooling holding the private token, not an end user, so the
   * enumeration concern behind `SignupWithPassword.InvalidDetails` doesn't apply.
   */
  ChangeEmail: {
    /** No user has `currentEmail`. */
    UserNotFound: '012301',
    /** `newEmail` already belongs to another user. */
    EmailInUse: '012302',
    /** `newEmail` is the same address as `currentEmail` (after normalizing). */
    SameEmail: '012303',
  },
} as const;

export const AUTH_CATALOG: Record<string, CatalogEntry> = {
  // ── Feature-level (AA = 00) ────────────────────────────────────────────────
  '010001': {
    httpStatus: 401,
    message: 'Your workspace is no longer available. Please sign in again.',
  },
  '010002': {
    httpStatus: 403,
    message: 'You no longer have access to this workspace.',
  },
  '010003': {
    httpStatus: 401,
    message: "Your account isn't set up correctly. Please contact support.",
  },
  '010004': {
    httpStatus: 401,
    message: "Your account isn't set up correctly. Please contact support.",
  },
  '010005': {
    httpStatus: 401,
    message: 'Your session has expired. Please sign in again.',
  },
  '010006': {
    httpStatus: 403,
    message: "You don't have permission to do that in this workspace.",
  },
  '010007': {
    httpStatus: 403,
    message: "You don't have permission to do that.",
  },
  '010008': {
    httpStatus: 401,
    message: 'Invalid or missing cron validation token.',
  },
  '010009': {
    httpStatus: 401,
    message: 'This session has been signed out. Please sign in again.',
  },
  '010010': {
    httpStatus: 403,
    message: "You've been removed from your workspace. Please contact your workspace owner for access.",
  },
  '010011': {
    httpStatus: 403,
    message: "We couldn't verify you're human. Please complete the security check and try again.",
  },

  // ── requestMagicLink (01) ──────────────────────────────────────────────────
  '010101': {
    httpStatus: 429,
    message: 'Too many sign-in requests. Please try again later.',
  },
  '010102': {
    httpStatus: 409,
    message: 'You already belong to a different organization.',
  },
  '010103': {
    httpStatus: 409,
    message: 'You already belong to a different organization.',
  },

  // ── verifyCode (02) ────────────────────────────────────────────────────────
  '010201': {
    httpStatus: 400,
    message: 'That code is invalid or has expired. Request a new one.',
  },
  '010202': {
    httpStatus: 400,
    message: 'Too many incorrect attempts. Request a new code.',
  },
  '010203': {
    httpStatus: 429,
    message: 'Too many attempts. Please try again later.',
  },
  '010204': {
    httpStatus: 409,
    message: 'You already belong to a different organization.',
  },
  '010205': {
    httpStatus: 409,
    message: 'You already belong to a different organization.',
  },

  // ── verifyLinkToken (03) ───────────────────────────────────────────────────
  '010301': {
    httpStatus: 400,
    message: 'This sign-in link is invalid or has expired.',
  },

  // ── completeProfile (04) ───────────────────────────────────────────────────
  '010401': {
    httpStatus: 400,
    message: 'Your session expired. Please start sign-in again.',
  },
  '010402': {
    httpStatus: 400,
    message: 'This invitation is no longer valid.',
  },
  '010403': {
    httpStatus: 500,
    message: 'Something went wrong on our end. Please try again.',
  },

  // ── oauthCallback (06) ─────────────────────────────────────────────────────
  '010601': {
    httpStatus: 409,
    message: 'You already belong to a different organization.',
  },
  '010602': {
    httpStatus: 409,
    message: 'You already belong to a different organization.',
  },

  // ── oauthOneTap (07) ───────────────────────────────────────────────────────
  '010701': {
    httpStatus: 401,
    message: 'Google sign-in could not be verified. Please try again.',
  },
  '010702': {
    httpStatus: 429,
    message: 'Too many sign-in attempts. Please try again later.',
  },

  // ── oauthExchange (09) ─────────────────────────────────────────────────────
  '010901': {
    httpStatus: 400,
    message: 'Sign-in could not be completed. Please try again.',
  },
  '010902': {
    httpStatus: 401,
    message: "We couldn't find your account.",
  },

  // ── refresh (10) ───────────────────────────────────────────────────────────
  '011001': {
    httpStatus: 401,
    message: 'Your session has expired. Please sign in again.',
  },

  // ── switchWorkspace (11) ───────────────────────────────────────────────────
  '011101': {
    httpStatus: 404,
    message: 'That workspace no longer exists.',
  },
  '011102': {
    httpStatus: 403,
    message: "You don't have access to that workspace.",
  },
  '011103': {
    httpStatus: 403,
    message: "You're not a member of that workspace.",
  },

  // ── acceptInvite (12) ──────────────────────────────────────────────────────
  '011201': {
    httpStatus: 400,
    message: 'This invitation is no longer valid.',
  },
  '011202': {
    httpStatus: 409,
    message: 'You already belong to a different organization.',
  },
  '011203': {
    httpStatus: 401,
    message: "We couldn't find your account.",
  },
  '011204': {
    httpStatus: 403,
    message: 'This invitation was sent to a different email address.',
  },

  // ── acceptShareableLink (13) ───────────────────────────────────────────────
  '011301': {
    httpStatus: 400,
    message: 'This invite link is invalid or has expired.',
  },
  '011302': {
    httpStatus: 409,
    message: 'You already belong to a different organization.',
  },
  '011303': {
    httpStatus: 401,
    message: "We couldn't find your account.",
  },

  // ── revokeSession (15) ─────────────────────────────────────────────────────
  '011501': {
    httpStatus: 400,
    message: "You can't sign out your current session here.",
  },
  '011502': {
    httpStatus: 404,
    message: 'That session no longer exists.',
  },

  // ── deleteAccount (18) ─────────────────────────────────────────────────────
  '011801': {
    httpStatus: 401,
    message: "We couldn't find your account.",
  },
  '011802': {
    httpStatus: 400,
    message:
      "You're the last owner of your organization. Make another member an owner before deleting your account.",
  },

  // ── loginWithPassword (19) ─────────────────────────────────────────────────
  '011901': {
    httpStatus: 401,
    message: 'That email or password is incorrect.',
  },
  '011902': {
    httpStatus: 429,
    message: 'Too many sign-in attempts. Please try again later.',
  },

  // ── resetPassword (21) ─────────────────────────────────────────────────────
  '012101': {
    httpStatus: 400,
    message: 'This password reset link is invalid or has expired. Please request a new one.',
  },

  // ── signupWithPassword (22) ────────────────────────────────────────────────
  '012201': {
    httpStatus: 400,
    message: 'Invalid details for the account you are trying to access.',
  },
  '012202': {
    httpStatus: 429,
    message: 'Too many sign-up attempts. Please try again later.',
  },
  '012203': {
    httpStatus: 400,
    message: 'Invalid or expired code. Please try again.',
  },
  '012204': {
    httpStatus: 403,
    message:
      "This email address isn’t eligible for sign-up. Please use a valid email address to create your account, or contact support if you believe this is a mistake.",
  },
  '012205': {
    httpStatus: 400,
    message: 'Email aliases (addresses containing "+") are not supported for sign-up. Please use your primary email address.',
  },

  // ── changeEmail (23, private support route) ────────────────────────────────
  '012301': {
    httpStatus: 404,
    message: 'No user has this email address.',
  },
  '012302': {
    httpStatus: 409,
    message: 'This email address already belongs to another user.',
  },
  '012303': {
    httpStatus: 422,
    message: 'The new email address is the same as the current one.',
  },
};
